import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core'
import { Observable, Subject } from 'rxjs'
import {
  IAppPluginWidget,
  ModelService,
  IPluginPermissions,
  IPublicViewConfiguration,
  IPublicViewWidget,
  IPlugin,
	PluginInjectorService,
	PluginInjectorWidget,
	PluginCommunicationService
} from 'core-client/api'
import { SharedModule } from '../../../shared/shared.module'
import { MatDialog } from '@angular/material/dialog'
import { SwiperOptions } from 'swiper/types'
import { SwiperDirective } from '../../../shared/swiper.directive'
import { animate, state, style, transition, trigger } from '@angular/animations'

@Component({
    selector: 'app-public-main',
    templateUrl: './main.component.html',
    imports: [SharedModule, SwiperDirective],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./main.component.scss'],
    animations: [
        trigger('hoverAnimation', [
            state('hidden', style({
                opacity: 0,
                visibility: 'hidden'
            })),
            state('visible', style({
                opacity: 1,
                visibility: 'visible'
            })),
            transition('hidden <=> visible', [
                animate('0.3s ease-in-out')
            ])
        ])
    ]
})
export class MainComponent implements OnInit, OnDestroy {
  @ViewChildren('widgetItems', { read: ElementRef }) widgetItems!: QueryList<ElementRef>;
  widgetInFullscreen: boolean = false;
  private ngUnsubscribe$: Subject<void> = new Subject<void>()
  @Input() publicViewConfiguration!: Observable<IPublicViewConfiguration>;
  private publicViewWidgets: IPublicViewWidget[] = [];
  @Input() isMobileView: boolean = false;
  private permissions: IPluginPermissions[] = []
  private widgetHeaderDisplayTimeout: ReturnType<typeof setTimeout> | null = null;

  // swiper config
  swiperConfig: SwiperOptions = {
    initialSlide: 0,
    spaceBetween: 10,
    pagination: true,
    navigation: false, // Show navigation arrows
    virtual: false
  };

  constructor(
    public modelService: ModelService,
    public dialog: MatDialog,
    public pluginInjectorService: PluginInjectorService,
    private pluginCommunicationService: PluginCommunicationService
  ) {}

  async ngOnInit(): Promise<void> {
    // load dashboard widgets of user as soon as public view configuration has been set
    this.publicViewConfiguration.subscribe(async (configuration: IPublicViewConfiguration) => {
      this.publicViewWidgets = configuration.widgets;
      if (this.publicViewWidgets.length > 0) {
          await this.loadDashboardWidgets();
      }
    })

    // listen for widget fullscreen change events
    document.addEventListener('fullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.addEventListener('webkitfullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.addEventListener('mozfullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.addEventListener('msfullscreenchange', this.onWidgetFullscreenChange.bind(this));
  }

  ngOnDestroy(): void {
    // cleanup widget fullscreen event listeners
    document.removeEventListener('fullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.removeEventListener('webkitfullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.removeEventListener('mozfullscreenchange', this.onWidgetFullscreenChange.bind(this));
    document.removeEventListener('msfullscreenchange', this.onWidgetFullscreenChange.bind(this));

    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private onWidgetFullscreenChange() {
    this.widgetInFullscreen = !!document.fullscreenElement;
    if (this.widgetInFullscreen && this.widgetHeaderDisplayTimeout == null) {
      this.widgetHeaderDisplayTimeout = setTimeout(() => {
        // after timeout set 'isHeaderHovered' flag of all widgets to 'false' in order to hide the header
        for (const widget of this.pluginInjectorService.widgetService.getWidgets()) {
          widget.isHeaderHovered = false;
        }
        this.clearWidgetHeaderDisplayTimeout();
      }, 3000);
    }
  }

  private async loadDashboardWidgets(): Promise<void> {
    // we currently only support one widget at a time in a public view
    const publicViewWidget = this.publicViewWidgets[0];

    const plugins: IPlugin[] = await this.modelService._plugin.listAll(`name=${publicViewWidget.pluginName.toUpperCase()}`)
    if (plugins.length > 0) {
      // fake appPluginWidget (not existing in DB)
      const appPluginWidget: IAppPluginWidget = {
        _id: '-1',
        enabled: true,
        name: (!publicViewWidget.widgetType) ? "" : publicViewWidget.widgetType.charAt(0).toUpperCase() + publicViewWidget.widgetType.slice(1).toLowerCase(), // e.g. 'Leaderboard'
        class: publicViewWidget.widgetType.toUpperCase(), // e.g. 'LEADERBOARD'
        layout: {
          position_x: 0,
          position_y: 0,
          columns: 30,
          rows: 30,
          width_px: 0,
          height_px: 0
        },
        settings: {
          showWidgetTitle: true,
          widgetHeader: {
            alwaysDisplayed: true,
            onHoverDisplayed: false,
            alwaysOff: false,
            transparent: false
          },
          leaderboardId: publicViewWidget.settings['selected_id']
        },
        app_plugin: '-1'
      }

      const widget: PluginInjectorWidget.IWidget = {
        _id: appPluginWidget._id,
        name: appPluginWidget.name,
        x: appPluginWidget.layout.position_x,
        y: appPluginWidget.layout.position_y,
        rows: appPluginWidget.layout.rows,
        cols: appPluginWidget.layout.columns,
        height: appPluginWidget.layout.height_px,
        width: appPluginWidget.layout.width_px,
        pluginId: plugins[0]._id,
        pluginModule: String(appPluginWidget.class),
        appPluginWidget: appPluginWidget,
        isHeaderHovered: false,
        // marks this widget as public (not linked to any appPluginWidget on any dashboard)
        publicView: {
          enabled: true,
          settings: publicViewWidget.settings
        }
      }

      this.pluginInjectorService.widgetService.setWidgets([widget])
    }
  }

  showWidgetHeader(widget: PluginInjectorWidget.IWidget): boolean {
    const widgetPermissions = this.permissions.find((pluginPermissions: IPluginPermissions) => pluginPermissions.plugin_id === widget.pluginId)
    if (widgetPermissions) {
      const showWidgetHeader = this.pluginInjectorService.widgetService.showWidgetHeader(widget, widgetPermissions)
      if (!showWidgetHeader) {
        return showWidgetHeader
      }

      return widget.appPluginWidget.settings.widgetHeader.alwaysDisplayed || (widget.appPluginWidget.settings.widgetHeader.onHoverDisplayed && widget.isHeaderHovered)
    }

    return widget.appPluginWidget.settings.widgetHeader.alwaysDisplayed || (widget.appPluginWidget.settings.widgetHeader.onHoverDisplayed && widget.isHeaderHovered)
  }

  private clearWidgetHeaderDisplayTimeout(): void {
    if (this.widgetHeaderDisplayTimeout != null) {
      clearTimeout(this.widgetHeaderDisplayTimeout);
      this.widgetHeaderDisplayTimeout = null;
    }
  }

  onWidgetHover(widget: PluginInjectorWidget.IWidget): void {
    widget.isHeaderHovered = true;
    if ((this.isMobileView || this.widgetInFullscreen) && this.widgetHeaderDisplayTimeout == null) {
      this.widgetHeaderDisplayTimeout = setTimeout(() => {
        widget.isHeaderHovered = false;
        this.clearWidgetHeaderDisplayTimeout();
      }, 3000);
    }
  }

  onWidgetHoverCancel(widget: PluginInjectorWidget.IWidget): void {
    widget.isHeaderHovered = false;
    this.clearWidgetHeaderDisplayTimeout();
  }

  onSwiperSlideChange(): void {
    // set the 'isHeaderHovered' flag to false on all widgets, since in mobile mode only one widget can be displayed at the same time and on slide change any hovering is canceled
    for (const widget of this.pluginInjectorService.widgetService.getWidgets()) {
      widget.isHeaderHovered = false;
    }
    this.clearWidgetHeaderDisplayTimeout();
  }

  async toggleFullscreen(widgetIndex: number): Promise<void> {
    if (!document.fullscreenEnabled) {
      console.warn('Fullscreen API is not supported in this browser.');
      return;
    }

    // Get the widget at this index
    const widgets = this.pluginInjectorService.widgetService.getWidgets();
    const widget = widgets[widgetIndex];

    // Check if this is a phone widget and try to toggle video fullscreen first
    if (widget && widget.pluginModule === 'PHONE') {
      try {
        // Request phone widget to try toggling video fullscreen
        const handled = await this.pluginCommunicationService.request(
          'addboard',
          'astimax',
          'tryToggleVideoFullscreen',
          {}
        ).toPromise();

        // If phone widget handled video fullscreen, don't do widget fullscreen
        if (handled) {
          console.log('[PublicDashboard] Video fullscreen toggled by phone widget');
          return;
        }
      } catch (error) {
        console.warn('[PublicDashboard] Phone widget did not handle video fullscreen, falling back to widget fullscreen');
      }
    }

    // Fallback: Do normal widget fullscreen
    const element = this.widgetItems.toArray()[widgetIndex].nativeElement;
    if (!document.fullscreenElement) {
      if (element.requestFullscreen) {
        element.requestFullscreen().catch((error: any) => {
          console.error(`Error attempting to enable widget full-screen mode: ${error.message} (${error.name})`);
        });
      /* Firefox */
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen().catch((error: any) => {
          console.error(`Error attempting to enable widget full-screen mode for Firefox: ${error.message} (${error.name})`);
        });
      /* Chrome, Safari and Opera */
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen().catch((error: any) => {
          console.error(`Error attempting to enable widget full-screen mode for WebKit: ${error.message} (${error.name})`);
        });
      /* IE/Edge */
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen().catch((error: any) => {
          console.error(`Error attempting to enable widget full-screen mode for IE/Edge: ${error.message} (${error.name})`);
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch((error: any) => {
          console.error(`Error attempting to exit widget full-screen mode: ${error.message} (${error.name})`);
        });
      /* Firefox */
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen().catch((error: any) => {
          console.error(`Error attempting to exit widget full-screen mode for Firefox: ${error.message} (${error.name})`);
        });
      /* Chrome, Safari and Opera */
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen().catch((error: any) => {
          console.error(`Error attempting to exit widget full-screen mode for WebKit: ${error.message} (${error.name})`);
        });
      /* IE/Edge */
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen().catch((error: any) => {
          console.error(`Error attempting to exit widget full-screen mode for IE/Edge: ${error.message} (${error.name})`);
        });
      }
    }
  }
}
